import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { TOKEN_STORAGE_KEYS } from '@onboarding-dashboard/shared';
import type { AuthUser } from '@onboarding-dashboard/shared';
import { authService } from '../services/auth.js';
import { apiClient } from '../services/apiClient.js';
import type { AuthContextValue, RegisterFormData } from '../types/auth.js';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = user !== null;

  // On mount: check for existing token and load user
  useEffect(() => {
    const MAX_INIT_RETRIES = 3;
    const initAuth = async (): Promise<void> => {
      const accessToken = localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      for (let attempt = 0; attempt < MAX_INIT_RETRIES; attempt++) {
        if (attempt > 0) setIsReconnecting(true);
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.data) {
            setUser(response.data);
            setIsReconnecting(false);
            setIsLoading(false);
            return;
          }
          // Auth-related failure (invalid token, etc.) — don't retry
          if (response.error?.code !== 'NETWORK_ERROR') break;
        } catch {
          // Network error — retry
        }
        if (attempt < MAX_INIT_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, 1500 * 2 ** attempt));
        }
      }

      // All retries exhausted or auth failure
      apiClient.clearTokens();
      setIsReconnecting(false);
      setIsLoading(false);
    };
    void initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.login({ email, password });
      if (response.success && response.data) {
        localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, response.data.tokens.accessToken);
        localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, response.data.tokens.refreshToken);
        setUser(response.data.user);
      } else {
        setError(response.error?.message ?? 'Login failed');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (data: RegisterFormData): Promise<void> => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.register({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        invitationToken: data.invitationToken,
      });
      if (response.success && response.data) {
        localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, response.data.tokens.accessToken);
        localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, response.data.tokens.refreshToken);
        setUser(response.data.user);
      } else {
        setError(response.error?.message ?? 'Registration failed');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async (credential: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authService.googleAuth(credential);
      if (response.success && response.data) {
        localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, response.data.tokens.accessToken);
        localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, response.data.tokens.refreshToken);
        setUser(response.data.user);
      } else {
        setError(response.error?.message ?? 'Google sign-in failed');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const registerWithGoogle = useCallback(
    async (credential: string, inviteToken: string): Promise<void> => {
      setError(null);
      setIsLoading(true);
      try {
        const response = await authService.googleAuth(credential, inviteToken);
        if (response.success && response.data) {
          localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, response.data.tokens.accessToken);
          localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, response.data.tokens.refreshToken);
          setUser(response.data.user);
        } else {
          setError(response.error?.message ?? 'Google registration failed');
        }
      } catch {
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const logout = useCallback((): void => {
    void authService.logout();
    apiClient.clearTokens();
    setUser(null);
    setError(null);
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
      }
    } catch {
      // silently fail — user stays as-is
    }
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const hasRole = useCallback(
    (role: string | string[]): boolean => {
      if (!user) return false;
      const rolesToCheck = Array.isArray(role) ? role : [role];
      const allUserRoles: string[] = [...(user.role ? [user.role] : []), ...(user.roles ?? [])];
      return rolesToCheck.some((r) => allUserRoles.includes(r));
    },
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      isReconnecting,
      error,
      login,
      register,
      loginWithGoogle,
      registerWithGoogle,
      logout,
      clearError,
      hasRole,
      refreshUser,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      isReconnecting,
      error,
      login,
      register,
      loginWithGoogle,
      registerWithGoogle,
      logout,
      clearError,
      hasRole,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
